import { useEffect } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/feature/ComicCard";
import SectionHeading from "@/components/feature/SectionHeading";
import BackToTop from "@/components/feature/BackToTop";
import { comics, categories, heroImage, ctaImage } from "@/mocks/comics";

const todayList = comics.slice(0, 8);
const rankList = [...comics].sort((a, b) => b.score - a.score).slice(0, 6);
const editorPicks = [comics[0], comics[4], comics[6], comics[10]];
const serialList = [...comics].sort((a, b) => Number(b.views.replace("万", "")) - Number(a.views.replace("万", ""))).slice(0, 8);

const testimonials = [
  { name: "漫友·小鹿", role: "追更党", text: "每天打开第一页就能看到今日更新，追的几部漫画一页全收，再也不用到处翻网站了。" },
  { name: "漫友·阿飞", role: "淘漫老手", text: "编辑推荐的水准很高，跟着推荐入坑的《霓虹侦探社》直接封神，评分也很靠谱。" },
  { name: "漫友·糖糖", role: "治愈系爱好者", text: "榜单和分类都整理得清清楚楚，找治愈系漫画特别方便，画风和题材都很对我的胃口。" },
  { name: "漫友·老猫", role: "潜水读者", text: "首页信息密度刚刚好，今天有什么新番、什么在涨热度一眼就能看到，体验很舒服。" },
];

const faqs = [
  { q: "今日更新多久刷新一次？", a: "卡通动漫第一页的今日更新内容池每日自动刷新，通常在每天凌晨到上午陆续入库，你可以随时回来查看最新上架的作品与章节。" },
  { q: "热门榜单是怎么排出来的？", a: "热门榜单综合了作品的点击量、收藏数、评分与讨论热度，结合近期的更新活跃度动态计算，确保反映当下真正受欢迎的作品。" },
  { q: "编辑推荐和热门榜单有什么区别？", a: "热门榜单以数据为导向，反映大众关注度；编辑推荐则由我们的内容团队人工筛选，更看重作品的品质、题材的多样性以及独特的阅读体验。" },
  { q: "想看的漫画没找到怎么办？", a: "欢迎通过「联系我们」页面告诉我们，也可以使用站内搜索按分类浏览。我们会持续扩充内容池，尽量满足大家的追更需求。" },
  { q: "这里的评分可靠吗？", a: "评分综合了编辑评分与读者反馈，我们坚持「只推好作品」的原则，避免注水与刷分，力求让每一个分数都真实可信。" },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const siteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "卡通动漫第一页",
  description: "卡通动漫第一页聚合今日更新、热门榜单与编辑推荐，高分漫画、人气连载一页全收。",
  potentialAction: {
    "@type": "SearchAction",
    target: "/today?q={search_term_string}",
    "query-input": "required name=search_term_string",
  },
};

export default function Home() {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify([siteSchema, faqSchema]);
    script.id = "home-ld";
    const old = document.getElementById("home-ld");
    if (old) old.remove();
    document.head.appendChild(script);
    return () => {
      document.getElementById("home-ld")?.remove();
    };
  }, []);

  return (
    <>
      <Seo
        title="卡通动漫第一页 - 今日更新/热门榜单/编辑推荐"
        description="卡通动漫第一页聚合今日更新、热门榜单与编辑推荐，高分漫画、人气连载一页全收，每日刷新内容池，帮你快速找到想追的那部漫画。"
        keywords="卡通动漫第一页,今日更新,热门榜单,编辑推荐,人气连载,高分漫画"
      />
      <Navbar />

      {/* Hero */}
      <section className="relative flex min-h-[560px] w-full items-center justify-center overflow-hidden md:min-h-[660px]" aria-label="品牌介绍">
        <img src={heroImage} alt="卡通动漫第一页动漫聚合平台主题图" className="absolute inset-0 h-full w-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/35 to-background-950/70"></div>
        <div className="relative z-10 w-full px-4 pt-24 pb-16 text-center md:pt-32 md:pb-20">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent-400/95 px-4 py-1.5 text-sm font-medium text-foreground-950">
            <i className="ri-flashlight-fill" style={{ fontSize: "15px" }}></i>
            今日更新 · 热门榜单 · 编辑推荐 一页全收
          </span>
          <h1 className="mx-auto mt-6 max-w-4xl font-display text-4xl leading-tight text-white md:text-6xl">
            卡通动漫第一页，<br className="hidden md:block" /> 找到你想追的那部
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/90 md:text-lg">
            高分漫画、人气连载、今日更新与编辑推荐聚合一处，每日刷新内容池。点开卡通动漫第一页，快速锁定你的下一部心头好。
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/today"
              className="w-full rounded-full bg-primary-500 px-8 py-3.5 text-sm font-medium whitespace-nowrap text-background-50 hover:bg-primary-600 transition-colors sm:w-auto"
            >
              查看今日更新 <i className="ri-arrow-right-line" style={{ fontSize: "15px" }}></i>
            </Link>
            <Link
              to="/rank"
              className="w-full rounded-full bg-white/15 px-8 py-3.5 text-sm font-medium whitespace-nowrap text-white ring-1 ring-white/40 backdrop-blur hover:bg-white/25 transition-colors sm:w-auto"
            >
              浏览热门榜单
            </Link>
          </div>
        </div>
      </section>

      <main>
        {/* 今日更新 */}
        <section className="container-x py-12 md:py-16" aria-label="今日更新">
          <SectionHeading
            icon="ri-flashlight-fill"
            title="今日更新"
            desc="每日刷新内容池，第一时间看到最新上架与连载"
            to="/today"
            accent
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {todayList.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </section>

        {/* 热门榜单 */}
        <section className="bg-background-100 py-12 md:py-16" aria-label="热门榜单">
          <div className="container-x">
            <SectionHeading icon="ri-fire-fill" title="热门榜单" desc="依据评分与热度，帮你锁定当下最火的高分漫画" to="/rank" />
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {rankList.map((c, i) => (
                <ComicCard key={c.id} comic={c} rank={i + 1} />
              ))}
            </div>
          </div>
        </section>

        {/* 编辑推荐 */}
        <section className="container-x py-12 md:py-16" aria-label="编辑推荐">
          <SectionHeading icon="ri-star-smile-fill" title="编辑推荐" desc="内容团队精选，每一部都值得细细品读" to="/recommend" accent />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {editorPicks.map((c) => (
              <Link
                key={c.id}
                to={`/comic/${c.id}`}
                className="group flex flex-col overflow-hidden rounded-xl bg-background-100 ring-1 ring-background-200 transition-transform duration-300 hover:-translate-y-1 hover:ring-accent-300 sm:flex-row"
                aria-label={`查看推荐作品《${c.title}》`}
              >
                <div className="relative aspect-[16/10] w-full overflow-hidden sm:aspect-auto sm:w-2/5">
                  <img src={c.cover} alt={`编辑推荐《${c.title}》封面`} title={`${c.title} 编辑推荐`} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-foreground-950">
                    <i className="ri-star-smile-fill" style={{ fontSize: "13px" }}></i> 编辑精选
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5 md:p-6">
                  <span className="text-xs text-accent-700">{c.category} · {c.author}</span>
                  <h3 className="mt-2 font-display text-xl text-foreground-950 group-hover:text-accent-700 transition-colors">{c.title}</h3>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">{c.detail}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                    {c.tags.slice(0, 3).map((t) => (
                      <span key={t} className="rounded-full bg-secondary-100 px-2.5 py-1 text-secondary-800">{t}</span>
                    ))}
                    <span className="ml-auto flex items-center gap-1 text-primary-700">
                      立即阅读 <i className="ri-arrow-right-line" style={{ fontSize: "14px" }}></i>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* 人气连载 */}
        <section className="bg-background-100 py-12 md:py-16" aria-label="人气连载">
          <div className="container-x">
            <SectionHeading icon="ri-group-fill" title="人气连载" desc="追更党必看，热度居高不下的高分连载" to="/rank" />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {serialList.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </div>
        </section>

        {/* 内容分类 */}
        <section className="container-x py-12 md:py-16" aria-label="内容分类">
          <SectionHeading icon="ri-archive-drawer-fill" title="内容分类" desc="按题材快速找到你喜欢的漫画类型" />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {categories.slice(1).map((cat, i) => {
              const list = comics.filter((c) => c.category === cat).slice(0, 2);
              return (
                <Link
                  key={cat}
                  to={`/today?cat=${encodeURIComponent(cat)}`}
                  className="group relative flex h-36 flex-col justify-end overflow-hidden rounded-xl p-5 text-background-50 transition-transform duration-300 hover:-translate-y-1"
                  aria-label={`浏览${cat}分类`}
                >
                  {list[0] ? (
                    <>
                      <img src={list[0].cover} alt={`${cat}分类封面图`} className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10"></div>
                    </>
                  ) : (
                    <div className={`absolute inset-0 ${["bg-primary-500", "bg-accent-500", "bg-secondary-500"][i % 3]}`}></div>
                  )}
                  <div className="relative">
                    <h3 className="font-display text-xl text-white">{cat}</h3>
                    <span className="mt-1 flex items-center gap-1 text-xs text-white/85">
                      {list.length ? `${list.length} 部精选` : "查看内容"} <i className="ri-arrow-right-line" style={{ fontSize: "13px" }}></i>
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* 用户口碑 */}
        <section className="bg-secondary-50 py-12 md:py-16" aria-label="用户口碑">
          <div className="container-x">
            <SectionHeading icon="ri-chat-heart-fill" title="用户口碑" desc="追更党与淘漫老手的真实评价" />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {testimonials.map((t) => (
                <figure key={t.name} className="rounded-xl bg-background-50 p-5 ring-1 ring-secondary-200">
                  <div className="flex items-center gap-1 text-accent-500">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <i key={s} className="ri-star-fill" style={{ fontSize: "14px" }}></i>
                    ))}
                  </div>
                  <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">“{t.text}”</blockquote>
                  <figcaption className="mt-4 flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-sm text-primary-700">
                      <i className="ri-user-fill" style={{ fontSize: "15px" }}></i>
                    </span>
                    <div>
                      <div className="text-sm font-medium text-foreground-950">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="container-x py-12 md:py-16" aria-label="常见问题">
          <SectionHeading icon="ri-question-answer-fill" title="常见问题" desc="关于卡通动漫第一页，你想知道的都在这里" />
          <div className="mx-auto grid max-w-4xl grid-cols-1 gap-4">
            {faqs.map((f, i) => (
              <details key={f.q} className="group rounded-xl bg-background-100 px-5 py-4 ring-1 ring-background-200 open:ring-primary-200" open={i === 0}>
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-base font-medium text-foreground-950">
                  <span className="flex items-center gap-3">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-xs text-background-50">{String(i + 1).padStart(2, "0")}</span>
                    {f.q}
                  </span>
                  <i className="ri-add-line text-foreground-400 transition-transform duration-300 group-open:rotate-45" style={{ fontSize: "18px" }}></i>
                </summary>
                <p className="mt-3 pl-10 text-sm leading-relaxed text-foreground-600">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="relative mt-4 mb-4 overflow-hidden" aria-label="关注我们">
          <img src={ctaImage} alt="卡通动漫第一页欢迎探索更多漫画" className="absolute inset-0 h-full w-full object-cover object-top" />
          <div className="absolute inset-0 bg-primary-950/70"></div>
          <div className="relative container-x flex flex-col items-center gap-6 py-16 text-center md:py-20">
            <h2 className="max-w-2xl font-display text-2xl text-background-50 md:text-4xl">今日份好漫画，已经为你准备好</h2>
            <p className="max-w-xl text-sm leading-relaxed text-background-100/85 md:text-base">高分漫画、人气连载、今日更新一页全收，每天都有新内容等你发现。</p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link to="/today" className="w-full rounded-full bg-accent-400 px-8 py-3.5 text-sm font-medium whitespace-nowrap text-foreground-950 hover:bg-accent-500 transition-colors sm:w-auto">
                去今日更新逛逛
              </Link>
              <Link to="/about" className="w-full rounded-full bg-white/15 px-8 py-3.5 text-sm font-medium whitespace-nowrap text-white ring-1 ring-white/40 backdrop-blur hover:bg-white/25 transition-colors sm:w-auto">
                了解我们
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}